import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import PortalModal from 'components/Shared/PortalModal'
import {
  findCreativeByTcKey,
  getTasteClusterOptionsFromSetup,
  getVariantsForCreative,
  mergePageInfo,
} from 'domain/adPreview/fromSetup'
import Header from './Header'
import ImageSidebar from './ImageSidebar'
import PlacementGroups from './PlacementGroups'

// "Preview Ads" modal — header (TC dropdown + platform filter + close), the
// "Images" sidebar, and the "Ad Placements" section list. Driven by the real
// `setup` from `CampaignSetupContext`; fixture data is still available via
// `pages/dev/AdPreviewHarness.jsx` for isolated debugging.
const PreviewAdsModal = ({
  isOpen,
  onClose,
  creatives,
  setup,
  pageInfo,
  disabledVariations = {},
  onToggleVariation,
}) => {
  const [platformFilter, setPlatformFilter] = useState('all')
  const [sidebarExpanded, setSidebarExpanded] = useState(true)

  const tasteClusterOptions = useMemo(
    () => getTasteClusterOptionsFromSetup({ creatives }),
    [creatives],
  )
  const [selectedTcKey, setSelectedTcKey] = useState(tasteClusterOptions[0]?.value)

  // defensively fall back to the first available TC option if the
  // currently selected one has been removed.
  const effectiveTcKey = tasteClusterOptions.some((option) => option.value === selectedTcKey)
    ? selectedTcKey
    : tasteClusterOptions[0]?.value

  useEffect(() => {
    if (effectiveTcKey !== selectedTcKey) {
      setSelectedTcKey(effectiveTcKey)
    }
  }, [effectiveTcKey, selectedTcKey])

  const rawCreative = findCreativeByTcKey(creatives, effectiveTcKey)
  const creative = useMemo(() => mergePageInfo(rawCreative, pageInfo), [rawCreative, pageInfo])
  const variants = useMemo(() => getVariantsForCreative(rawCreative, setup), [rawCreative, setup])
  const activePlatforms = useMemo(
    () => [...new Set(variants.map((variant) => variant.split('-')[0]))],
    [variants],
  )

  // Clamp the platform filter to something still visible whenever the
  // active platform set changes (e.g. switching TC changes campaign_type).
  useEffect(() => {
    if (platformFilter !== 'all' && !activePlatforms.includes(platformFilter)) {
      setPlatformFilter('all')
    }
  }, [activePlatforms, platformFilter])

  // No asset preselected by default — an unset selection means "show every
  // card", matching PlacementGroups' fallback-to-unfiltered behavior below.
  const [selectedMediaId, setSelectedMediaId] = useState(undefined)
  useEffect(() => {
    setSelectedMediaId(undefined)
  }, [creative])

  return (
    <PortalModal
      isOpen={isOpen}
      onClickOutside={onClose}
      className="full-height"
      showCloseButton={false}
    >
      <div className="preview-ads-modal">
        <Header
          tasteClusterOptions={tasteClusterOptions}
          selectedTc={effectiveTcKey}
          onTcChange={setSelectedTcKey}
          activePlatforms={activePlatforms}
          platformFilter={platformFilter}
          onPlatformFilterChange={setPlatformFilter}
          onClose={onClose}
        />
        <div className="preview-ads-modal__body">
          <ImageSidebar
            creative={creative}
            expanded={sidebarExpanded}
            onToggleExpanded={() => setSidebarExpanded((prev) => !prev)}
            selectedMediaId={selectedMediaId}
            onSelectMedia={setSelectedMediaId}
          />
          <PlacementGroups
            creative={creative}
            variants={variants}
            platformFilter={platformFilter}
            selectedMediaId={selectedMediaId}
            disabledVariations={disabledVariations}
            onToggleVariation={
              onToggleVariation ? (ad) => onToggleVariation(ad, creative?.tc) : undefined
            }
          />
        </div>
      </div>
    </PortalModal>
  )
}

PreviewAdsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  creatives: PropTypes.arrayOf(PropTypes.object),
  setup: PropTypes.object,
  pageInfo: PropTypes.shape({
    page_name: PropTypes.string,
    profile_picture_url: PropTypes.string,
  }),
  disabledVariations: PropTypes.object,
  onToggleVariation: PropTypes.func,
}

export default PreviewAdsModal
